import React, { useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  XCircle,
  Download,
  ReceiptText,
  Loader2,
} from "lucide-react";
import html2canvas from "html2canvas";
import { getReceiptTemplate } from "../lib/receiptTemplates";

const fmtMoney = (value, currency = "") => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}${currency ? ` ${currency}` : ""}`;
};

const fmtDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return String(value || "");
  return date.toLocaleString("fr-FR");
};

const normalizeStatus = (value) => String(value || "").trim().toUpperCase();
const sanitizeFileName = (value) =>
  String(value || "").trim().replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 80);

function Brand({ template }) {
  const dark = template.dark;
  return (
    <div>
      <div className="text-[24px] font-black tracking-tight" style={{ color: dark ? "#fff" : "#061225" }}>
        MK<span style={{ color: template.variant === "minimal" ? "#111" : template.accent }}>TRANSFÈ</span>
      </div>
      <div className="mt-0.5 text-[9px] font-black uppercase tracking-[0.2em]" style={{ color: template.muted }}>
        Transfè lajan, pi rapid, pi serye
      </div>
    </div>
  );
}

function StatusBlock({ statusInfo, template, compact = false }) {
  const Icon = statusInfo.icon;
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "rounded-2xl px-4 py-3"}`}
      style={compact ? undefined : { background: template.dark ? "rgba(255,255,255,.04)" : template.surface, border: `1px solid ${template.border}` }}>
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
        style={{ background: statusInfo.soft, color: statusInfo.color }}>
        <Icon className={`h-5 w-5 ${statusInfo.spin ? "animate-spin" : ""}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-black" style={{ color: template.text }}>{statusInfo.title}</div>
        <div className="mt-0.5 text-[10px] leading-4" style={{ color: template.muted }}>{statusInfo.description}</div>
      </div>
      <span className="max-w-[42%] rounded-full border px-2.5 py-1 text-center text-[9px] font-black leading-4"
        style={{ color: statusInfo.color, borderColor: statusInfo.border, background: statusInfo.soft }}>
        {statusInfo.text}
      </span>
    </div>
  );
}

function RowsList({ rows, template, ledger = false }) {
  if (ledger) {
    return (
      <div className="grid grid-cols-2 gap-x-5 gap-y-1 px-4 py-3">
        {rows.map((row, index) => (
          <div key={`${row.label}-${index}`} className="min-w-0 border-b py-3" style={{ borderColor: template.border }}>
            <div className="text-[9px] font-black uppercase tracking-[0.12em]" style={{ color: template.muted }}>{row.label}</div>
            <div className="mt-1 break-words text-[12px] font-black" style={{ color: row.highlight ? "#16A36D" : template.text }}>{row.value}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="px-4">
      {rows.map((row, index) => (
        <div key={`${row.label}-${index}`} className="flex items-start justify-between gap-4 border-b py-2.5 last:border-0" style={{ borderColor: template.border }}>
          <span className="shrink-0 text-[11px] font-semibold" style={{ color: template.muted }}>{row.label}</span>
          <span className="max-w-[64%] break-words text-right text-[11px] font-bold"
            style={{ color: row.highlight ? "#16A36D" : template.text, fontWeight: row.strong || row.highlight ? 900 : 700 }}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function DetailBox({ rows, template, ledger = false, title = "Detay" }) {
  return (
    <div className="overflow-hidden rounded-2xl border" style={{ borderColor: template.border, background: template.dark ? "rgba(255,255,255,.015)" : "#fff" }}>
      <div className="flex items-center gap-2 px-4 py-2.5 text-[12px] font-black"
        style={{ background: template.variant === "minimal" ? "#111" : template.accent, color: "#fff" }}>
        <ReceiptText className="h-4 w-4" /> {title}
      </div>
      <RowsList rows={rows} template={template} ledger={ledger} />
    </div>
  );
}

function HeroAmount({ label, value, template, align = "center" }) {
  return (
    <div className={align === "left" ? "text-left" : "text-center"}>
      <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={{ color: template.muted }}>{label}</div>
      <div className="mt-1 text-[32px] font-black tracking-tight" style={{ color: template.variant === "orange" ? "#FF8C00" : template.dark ? "#fff" : template.accent }}>
        {value}
      </div>
    </div>
  );
}

function ReceiptBody({ template, statusInfo, rows, heroLabel, heroValue, reference, message, isFailed }) {
  const commonMessage = message ? (
    <div className="mt-4 rounded-2xl border px-4 py-3 text-[11px] font-semibold leading-5"
      style={{ borderColor: isFailed ? "#FECACA" : template.border, background: isFailed ? "#FEF2F2" : template.dark ? "rgba(255,255,255,.04)" : template.surface, color: isFailed ? "#B91C1C" : template.muted }}>
      {isFailed && <div className="mb-1 text-[9px] font-black uppercase tracking-[0.16em]">Rezon / Nòt</div>}
      <div className="whitespace-pre-wrap break-words">{message}</div>
    </div>
  ) : null;

  switch (template.variant) {
    case "dark":
      return <>
        <div className="px-5 pt-5"><StatusBlock statusInfo={statusInfo} template={template} /></div>
        <div className="mx-5 mt-4 rounded-3xl px-5 py-5" style={{ background: template.surface }}>
          <HeroAmount label={heroLabel} value={heroValue} template={template} align="left" />
          {reference && <div className="mt-2 text-[10px] font-bold" style={{ color: template.muted }}>Referans • {reference}</div>}
        </div>
        <div className="px-5 py-4"><DetailBox rows={rows} template={template} />{commonMessage}</div>
      </>;
    case "split":
      return <>
        <div className="px-5 pb-6 pt-5" style={{ background: template.accent }}>
          <StatusBlock statusInfo={statusInfo} template={{...template, text:"#fff", muted:"#D8E4FF", border:"rgba(255,255,255,.2)", surface:"rgba(255,255,255,.08)", dark:true}} compact />
          <div className="mt-5"><HeroAmount label={heroLabel} value={heroValue} template={{...template, dark:true, muted:"#D8E4FF"}} align="left" /></div>
        </div>
        <div className="-mt-2 rounded-t-[28px] bg-white px-5 py-5"><DetailBox rows={rows} template={template} />{commonMessage}</div>
      </>;
    case "orange":
      return <>
        <div className="border-l-[10px] px-5 py-5" style={{ borderColor: "#FF8C00" }}>
          <StatusBlock statusInfo={statusInfo} template={template} compact />
          <div className="mt-5 rounded-3xl px-5 py-4" style={{ background: template.surface }}><HeroAmount label={heroLabel} value={heroValue} template={template} align="left" /></div>
          <div className="mt-4"><DetailBox rows={rows} template={template} />{commonMessage}</div>
        </div>
      </>;
    case "ticket":
      return <>
        <div className="px-6 py-5"><StatusBlock statusInfo={statusInfo} template={template} compact /></div>
        <div className="px-6"><HeroAmount label={heroLabel} value={heroValue} template={template} /></div>
        <div className="mx-6 my-5 border-t-2 border-dashed" style={{ borderColor: template.border }} />
        <div className="px-6"><RowsList rows={rows} template={template} />{commonMessage}</div>
        <div className="mx-6 my-5 border-t-2 border-dashed" style={{ borderColor: template.border }} />
      </>;
    case "layered":
      return <>
        <div className="px-5 pb-16 pt-5" style={{ background: "linear-gradient(145deg,#001E5C,#1749A5,#2F65C3)" }}>
          <StatusBlock statusInfo={statusInfo} template={{...template, text:"#fff", muted:"#D8E4FF", border:"rgba(255,255,255,.2)", surface:"rgba(255,255,255,.08)", dark:true}} compact />
          <div className="mt-5"><HeroAmount label={heroLabel} value={heroValue} template={{...template, dark:true, muted:"#D8E4FF"}} align="left" /></div>
        </div>
        <div className="mx-4 -mt-10 rounded-[28px] bg-white px-4 py-5 shadow-xl"><DetailBox rows={rows} template={template} />{commonMessage}</div>
      </>;
    case "ledger":
      return <>
        <div className="px-5 py-5"><StatusBlock statusInfo={statusInfo} template={template} compact />
          <div className="mt-5 rounded-2xl border px-4 py-4" style={{ borderColor: template.border, background: template.surface }}><HeroAmount label={heroLabel} value={heroValue} template={template} align="left" /></div>
        </div>
        <div className="px-5 pb-4"><DetailBox rows={rows} template={template} ledger />{commonMessage}</div>
      </>;
    case "mobileDark":
      return <>
        <div className="px-5 py-5"><div className="rounded-[26px] px-5 py-5" style={{ background: template.surface }}>
          <StatusBlock statusInfo={statusInfo} template={template} compact />
          <div className="mt-5"><HeroAmount label={heroLabel} value={heroValue} template={template} align="left" /></div>
          {reference && <div className="mt-2 text-[10px] font-bold" style={{ color: template.muted }}>MKTRANSFÈ • {reference}</div>}
        </div></div>
        <div className="px-5 pb-5"><DetailBox rows={rows} template={template} />{commonMessage}</div>
      </>;
    case "verify":
      return <>
        <div className="px-5 py-5">
          <div className="rounded-[26px] border px-5 py-5" style={{ borderColor: template.border, background: template.surface }}>
            <StatusBlock statusInfo={statusInfo} template={template} compact />
            <div className="mt-4"><HeroAmount label={heroLabel} value={heroValue} template={template} align="left" /></div>
            {reference && <div className="mt-2 text-[10px] font-bold" style={{ color: template.muted }}>Kòd verifikasyon: {reference}</div>}
          </div>
          <div className="mt-4"><DetailBox rows={rows} template={template} />{commonMessage}</div>
        </div>
      </>;
    case "minimal":
      return <>
        <div className="px-6 py-5"><div className="border-t-2 pt-5" style={{ borderColor: "#111" }}><div className="flex items-start justify-between gap-4"><HeroAmount label={heroLabel} value={heroValue} template={template} align="left" /><div className="pt-2 text-[10px] font-black uppercase">{statusInfo.text}</div></div></div></div>
        <div className="px-6 pb-5"><RowsList rows={rows} template={template} />{commonMessage}</div>
      </>;
    case "classic":
    default:
      return <>
        <div className="px-5 pt-5"><StatusBlock statusInfo={statusInfo} template={template} /></div>
        <div className="mx-5 mt-4 rounded-3xl px-5 py-4" style={{ background: template.surface }}><HeroAmount label={heroLabel} value={heroValue} template={template} /></div>
        <div className="px-5 py-4"><DetailBox rows={rows} template={template} />{commonMessage}</div>
      </>;
  }
}

export default function OfficialReceipt({
  templateId = "RECEIPT_01",
  animationDurationMs = 780,
  service = "MKTRANSFE",
  status = "PENDING",
  reference = "",
  number = "",
  beneficiary = "",
  amount = null,
  currency = "",
  fee = null,
  total = null,
  totalCurrency = "",
  receiveAmount = null,
  receiveCurrency = "",
  exchangeRate = null,
  product = "",
  plan = "",
  createdAt = null,
  extraRows = [],
  message = "",
  onClose = null,
}) {
  const receiptRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const template = getReceiptTemplate(templateId);
  const normalizedStatus = normalizeStatus(status);

  const statusInfo = useMemo(() => {
    const completed = ["COMPLETED","SUCCESS","SUCCESSFUL","PAID","CONFIRMED","APPROVED"].includes(normalizedStatus);
    const failed = ["FAILED","REJECTED","CANCELLED","CANCELED","EXPIRED","DECLINED"].includes(normalizedStatus);
    const review = ["PENDING_REVIEW","REVIEW","MANUAL_REVIEW","AWAITING_VERIFICATION","WAITING_VERIFICATION","VERIFIED_RECEIPT","KYC_PENDING","PENDING_KYC","UNDER_REVIEW"].includes(normalizedStatus);
    const processing = ["PROCESSING","IN_PROGRESS","SUBMITTED","SUBMITTING","QUEUED"].includes(normalizedStatus);
    const awaitingReceipt = ["AWAITING_RECEIPT","RECEIPT_REQUIRED"].includes(normalizedStatus);
    if (completed) return { title:"Tranzaksyon konfime", text:"REYISI", description:"Operasyon an fini avèk siksè.", icon:CheckCircle2, color:"#059669", soft:"#ECFDF5", border:"#A7F3D0" };
    if (failed) return { title:"Tranzaksyon echwe", text:"ECHWE", description:"Operasyon an pa t finalize avèk siksè.", icon:XCircle, color:"#DC2626", soft:"#FEF2F2", border:"#FECACA" };
    if (review) return { title:"Ap tann verifikasyon", text:"AP TANN VERIFIKASYON", description:"Demann lan resevwa epi li ap tann verifikasyon.", icon:Clock3, color:"#7C3AED", soft:"#F5F3FF", border:"#DDD6FE" };
    if (processing) return { title:"Tranzaksyon ap trete", text:"AP TRETE", description:"Founisè a oswa MKTRANSFÈ ap trete operasyon an.", icon:Loader2, spin:true, color:"#0284C7", soft:"#F0F9FF", border:"#BAE6FD" };
    if (awaitingReceipt) return { title:"Ap tann resi", text:"AP TANN RESI", description:"Referans lan kreye. Voye prèv peman an pou kontinye.", icon:Clock3, color:"#D97706", soft:"#FFFBEB", border:"#FDE68A" };
    return { title:"Tranzaksyon an atant", text:"AN ATANT", description:"Demann lan anrejistre epi li poko finalize.", icon:Clock3, color:"#D97706", soft:"#FFFBEB", border:"#FDE68A" };
  }, [normalizedStatus]);

  const isFailed = ["FAILED","REJECTED","CANCELLED","CANCELED","EXPIRED","DECLINED"].includes(normalizedStatus);

  const isRechargeReceipt = useMemo(() => {
    const ref = String(reference || "").trim().toUpperCase();
    const svc = String(service || "").trim().toUpperCase();
    const prod = String(product || "").trim().toUpperCase();
    return ref.startsWith("RD-") || svc.includes("RECARGA") || svc.includes("RECHAJ") || svc.includes("MINIT") || prod.includes("RECARGA") || prod.includes("RECHAJ") || prod.includes("MINIT");
  }, [reference, service, product]);

  const rows = useMemo(() => {
    const result = [];
    if (service) result.push({ label:"Sèvis", value:service });
    if (number) result.push({ label:"Nimewo / Kont", value:number });
    if (!isRechargeReceipt && beneficiary && beneficiary !== number) result.push({ label:"Benefisyè", value:beneficiary });
    if (amount !== null && amount !== undefined) result.push({ label:"Montan", value:fmtMoney(amount,currency), strong:true });
    if (fee !== null && fee !== undefined && Number(fee) !== 0) result.push({ label:"Frè", value:fmtMoney(fee,currency) });
    if (receiveAmount !== null && receiveAmount !== undefined) result.push({ label:"Resevwa", value:fmtMoney(receiveAmount,receiveCurrency), highlight:true });
    if (product || isRechargeReceipt) {
      const name = String(product || service || "").replace(/^recarga\s*/i, "").replace(/^rechaj\s*/i, "").trim();
      result.push({ label:"Pwodwi", value:isRechargeReceipt ? `Rechaj${name ? ` ${name}` : ""}` : product });
    }
    if (plan) result.push({ label:"Plan", value:plan });
    if (exchangeRate !== null && exchangeRate !== undefined && Number(exchangeRate) > 0) result.push({ label:"To chanj", value:Number(exchangeRate).toLocaleString("fr-FR", { maximumFractionDigits:6 }) });
    if (reference) result.push({ label:"Referans", value:reference });
    result.push({ label:"Dat & lè", value:fmtDate(createdAt) });
    const allowed = ["metòd","method","rezo","network","txid","kont","wallet","memo","tag","email","node","network id"];
    (Array.isArray(extraRows) ? extraRows : []).forEach((row) => {
      if (!row?.label || row.value === undefined || row.value === null || row.value === "") return;
      const label = String(row.label).trim().toLowerCase();
      if (allowed.some((key) => label.includes(key))) result.push({ label:row.label, value:row.value });
    });
    return result.slice(0, 12);
  }, [service, number, beneficiary, isRechargeReceipt, amount, currency, fee, receiveAmount, receiveCurrency, product, plan, exchangeRate, reference, createdAt, extraRows]);

  const heroValue = receiveAmount !== null && receiveAmount !== undefined
    ? fmtMoney(receiveAmount, receiveCurrency)
    : total !== null && total !== undefined
      ? fmtMoney(total, totalCurrency || currency)
      : amount !== null && amount !== undefined
        ? fmtMoney(amount, currency)
        : "—";
  const heroLabel = receiveAmount !== null && receiveAmount !== undefined ? "Benefisyè ap resevwa" : total !== null && total !== undefined ? "Kantite total" : "Montan";

  const handleDownload = async () => {
    if (!receiptRef.current || isDownloading) return;
    const receiptElement = receiptRef.current;
    const actionElements = receiptElement.querySelectorAll(".mk-receipt-no-download");
    try {
      setIsDownloading(true);
      actionElements.forEach((element) => { element.dataset.previousDisplay = element.style.display || ""; element.style.display = "none"; });
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(receiptElement, {
        backgroundColor: template.dark ? "#07152a" : "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        windowWidth: receiptElement.scrollWidth,
        windowHeight: receiptElement.scrollHeight,
      });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/jpeg", 0.95);
      link.download = `MKTRANSFE-RESI-${sanitizeFileName(reference) || Date.now()}.jpg`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur téléchargement reçu JPG:", error);
      alert("Nou pa kapab telechaje resi a pou moman an.");
    } finally {
      actionElements.forEach((element) => { element.style.display = element.dataset.previousDisplay || ""; delete element.dataset.previousDisplay; });
      setIsDownloading(false);
    }
  };

  const duration = Math.max(0, Math.min(3000, Number(animationDurationMs || 780)));

  return (
    <div className="mk-official-receipt-overlay" style={{ "--mk-receipt-duration": `${duration}ms` }}>
      <div ref={receiptRef} id="mk-official-receipt" className={`mk-official-receipt-card mk-receipt-${template.variant}`}
        style={{ background: template.dark ? "#07152a" : "#fff", color: template.text, borderColor: template.border }}>
        <div className="flex items-center justify-between gap-3 border-b px-5 py-4" style={{ borderColor: template.border, background: template.variant === "layered" ? "#001E5C" : template.dark ? "#07152a" : "#fff" }}>
          <Brand template={template.variant === "layered" ? {...template, dark:true, muted:"#D8E4FF"} : template} />
          <div className="text-right"><div className="text-[9px] font-black uppercase tracking-[0.18em]" style={{ color: template.muted }}>Resi Ofisyèl</div><div className="mt-1 text-[9px] font-bold" style={{ color: template.muted }}>{template.id.replace("RECEIPT_", "Design ")}</div></div>
        </div>

        <ReceiptBody template={template} statusInfo={statusInfo} rows={rows} heroLabel={heroLabel} heroValue={heroValue} reference={reference} message={message} isFailed={isFailed} />

        <div className="px-5 pb-5">
          <div className="text-center text-[10px] leading-4" style={{ color: template.muted }}>
            Resi sa a se prèv dijital demann/operasyon an ak estati li nan moman sa a.<br />Estati a ka mete ajou jiskaske operasyon an finalize.
          </div>
          <div className="mk-receipt-no-download mt-4 grid gap-2 sm:grid-cols-2">
            <button type="button" onClick={handleDownload} disabled={isDownloading}
              className="flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-black text-white transition hover:brightness-110 disabled:opacity-50"
              style={{ background: template.variant === "minimal" ? "#111" : template.accent }}>
              {isDownloading ? <><Loader2 className="h-4 w-4 animate-spin" />Preparasyon...</> : <><Download className="h-4 w-4" />Telechaje JPG</>}
            </button>
            {onClose && <button type="button" onClick={onClose} className="rounded-xl border px-4 py-3 text-sm font-black transition hover:opacity-80"
              style={{ borderColor: template.border, background: template.dark ? "rgba(255,255,255,.04)" : "#fff", color: template.text }}>Fèmen</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
